import type { ReactElement } from 'react';
import { useContext, useLayoutEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type { publicApiType } from 'react-horizontal-scrolling-menu';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { BackgroundType } from '../../../types';
import { elements, getClassName } from '../../helpers';
import type { ForecastListItem, RequestResponse } from '../api/request';
import { ForecastDetail } from './forecast-detail';
import { ForecastItem } from './forecast-item';

export const Forecast = ({
  data
}: {
  data: RequestResponse<ForecastListItem>;
}) => {
  const { layout, backgroundType } = useSettings();
  const { list, city } = data;

  const [items, setItems] = useState<ReactElement<any, any>[]>([]);

  useLayoutEffect(() => {
    const result = (list || []).map((item, id) => (
      <ForecastItem key={id} data={item} city={city} />
    ));

    if (elements.showForecastDetail(layout)) {
      result.unshift(<ForecastDetail key="forecast-details" data={data} />);
    }

    setItems(result);
  }, [data, layout]);

  return (
    <Component _backgroundType={backgroundType}>
      {items.length && (
        <NoScrollbar>
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow}>
            {items}
          </ScrollMenu>
        </NoScrollbar>
      )}
    </Component>
  );
};

const NoScrollbar = styled('div')({
  '& .react-horizontal-scrolling-menu--scroll-container::-webkit-scrollbar': {
    display: 'none'
  },
  '& .react-horizontal-scrolling-menu--scroll-container': {
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none'
  }
});

const LeftArrow = () => {
  const { backgroundType, backgroundColor } = useSettings();
  const visibility = useContext<publicApiType>(VisibilityContext);

  const disabled = visibility.useLeftArrowVisible();

  return (
    <Prev
      onClick={() => visibility.scrollPrev()}
      _disabled={disabled}
      _backgroundType={backgroundType}
      _backgroundColor={backgroundColor}
    >
      <IconLeft
        _backgroundType={backgroundType}
        _backgroundColor={backgroundColor}
        viewBox="4 0 8 16"
        width="12"
        height="16"
      >
        <path
          d="M4.3,8.7l6,5.9c0.4,0.4,1.1,0.4,1.5,0c0.4-0.4,0.4-1.1,0-1.5L6.5,8l5.2-5.2c0.4-0.4,0.4-1.1,0-1.5
                    c-0.4-0.4-1.1-0.4-1.5,0l-6,6C3.9,7.7,3.9,8.3,4.3,8.7z"
        />
      </IconLeft>
    </Prev>
  );
};

const RightArrow = () => {
  const { backgroundType, backgroundColor } = useSettings();
  const visibility = useContext<publicApiType>(VisibilityContext);

  const disabled = visibility.useRightArrowVisible();

  return (
    <Next
      onClick={() => visibility.scrollNext('smooth', 'center')}
      _disabled={disabled}
      _backgroundType={backgroundType}
      _backgroundColor={backgroundColor}
    >
      <IconRight
        _backgroundType={backgroundType}
        _backgroundColor={backgroundColor}
        viewBox="4 0 8 16"
        width="12"
        height="16"
      >
        <path
          d="M11.7,7.3l-6-5.9c-0.4-0.4-1.1-0.4-1.5,0c-0.4,0.4-0.4,1.1,0,1.5L9.5,8l-5.2,5.2
                    c-0.4,0.4-0.4,1.1,0,1.5c0.4,0.4,1.1,0.4,1.5,0l6-6C12.1,8.3,12.1,7.7,11.7,7.3z"
        />
      </IconRight>
    </Next>
  );
};

const Component = styled.div.attrs<{
  _backgroundType: BackgroundType;
}>({
  className: getClassName('forecast', 'component')
})`
  overflow: hidden;
  margin-top: 24px;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    pointer-events: none;
  }

  & .react-horizontal-scrolling-menu--scroll-container {
    display: flex;
  }

  & .react-horizontal-scrolling-menu--item {
    padding: 0 10px;
  }

  & .react-horizontal-scrolling-menu--item:first-child {
    padding-left: 24px;
  }

  & .react-horizontal-scrolling-menu--item:last-child {
    padding-right: 20px;
  }

  & .react-horizontal-scrolling-menu--arrow-left,
  .react-horizontal-scrolling-menu--arrow-right {
    opacity: ${({ _backgroundType }) =>
      _backgroundType === 'color' ? '1' : '0'};
    background: red;
    transition: 0.3s ease all;
  }
`;

const Arrow = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
  _disabled?: boolean;
}>({
  className: getClassName('forecast', 'arrow')
})`
  ${({ _disabled }) =>
    _disabled &&
    css`
      opacity: 0 !important;
      pointer-events: none !important;
      visibility: hidden !important;
    `}

  color: #111;
  z-index: 10;
  width: ${({ _backgroundType }) =>
    _backgroundType === 'image' ? '30' : '60'}px;
  height: 100%;
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  opacity: 1;
  pointer-events: all;
  cursor: pointer;
  visibility: visible;
  transition: all 0.3s ease;
`;

const Prev = styled(Arrow).attrs({
  className: getClassName('forecast', 'prev')
})`
  left: 0;
  justify-content: flex-start;

  ${({ _backgroundType, _backgroundColor }) => `
    ${
      _backgroundType === 'color'
        ? `
          background: linear-gradient(to left, ${changeColorAlpha(
            _backgroundColor,
            -1
          )}, ${_backgroundColor} 55%);
        `
        : `
          background: rgba(0,0,0,0.4);
          border-radius: 0 8px 8px 0;
        `
    }
  `}

  &:hover {
    ${({ _backgroundType }) =>
      _backgroundType !== 'color' && 'background: rgba(0,0,0,0.5);'}
  }
`;

const Next = styled(Arrow).attrs({
  className: getClassName('forecast', 'next')
})`
  right: 0;
  justify-content: flex-end;

  ${({ _backgroundType, _backgroundColor }) => `
    ${
      _backgroundType === 'color'
        ? `
        background: linear-gradient(to right, ${changeColorAlpha(
          _backgroundColor,
          -1
        )}, ${_backgroundColor} 55%);
        `
        : `
          background: rgba(0,0,0,0.4);
          border-radius: 8px 0 0 8px;
        `
    }
  `}

  &:hover {
    ${({ _backgroundType }) =>
      _backgroundType !== 'color' && 'background: rgba(0,0,0,0.5);'}
  }
`;

const Icon = styled.svg.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
}>({
  className: getClassName('forecast', 'icon')
})`
  transition: 0.3s ease all;

  fill: ${({ _backgroundType, _backgroundColor }) =>
    _backgroundType === 'image' || !_backgroundColor
      ? '#fff'
      : getBrightInvertedColor(_backgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])};
`;

const IconLeft = styled(Icon).attrs({
  className: getClassName('forecast', 'iconLeft')
})`
  margin-left: 10px;

  ${Prev}:hover & {
    margin-left: 8px;
  }
`;

const IconRight = styled(Icon).attrs({
  className: getClassName('forecast', 'iconRight')
})`
  margin-right: 10px;

  ${Next}:hover & {
    margin-right: 8px;
  }
`;
